import { apiClient } from '@/lib/api-client';
import type {
  CreateOnlineOrderResponse,
  RazorpayRefund,
  GatewayCredentialsMasked,
} from '@bbpos/types';

export interface VerifyOnlinePaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface InitiateRefundRequest {
  bill_id: string;
  amount: number;
  reason: string;
  return_bill_id?: string;
  speed?: 'normal' | 'optimum';
}

export type TerminalPushStatus =
  | 'WAITING_FOR_PAYMENT'
  | 'SUCCESS'
  | 'DECLINED'
  | 'TIMEOUT'
  | 'ERROR'
  | 'CANCELLED';

export interface TerminalPush {
  push_id: string;
  terminal_id: string | null;
  terminal_name: string;
  amount: number;
  status: TerminalPushStatus;
  timeout_seconds: number;
  /** Dev mock (no terminal registered, non-production): confirms on first poll. */
  is_mock: boolean;
}

export interface TerminalPushState {
  push_id: string;
  status: TerminalPushStatus;
  transaction_reference: string | null;
  is_mock: boolean;
}

/**
 * Payment-gateway services (G1.2).
 *
 * Online-payment lifecycle:
 *   1. Cashier creates DRAFT bill
 *   2. createOnlineOrder(billId) → returns order_id + key_id for Razorpay SDK
 *   3. Client SDK handles card/UPI entry, returns signature
 *   4. verifyOnlinePayment(signature) → server verifies HMAC + atomically completes bill
 *
 * Dev-mock: when no tenant Razorpay creds are set (non-production), the
 * backend returns a mocked order. The client SDK will still be invoked but
 * with `is_mock: true` — the modal auto-resolves after a short delay.
 */
export const paymentsService = {
  // ─── Card / UPI terminal ───────────────────────────────────────────────────
  /** No terminal_id: the server uses the terminal registered at the location. */
  pushToTerminal(dto: { bill_id: string; amount: number; location_id: string }) {
    return apiClient<TerminalPush>('/payments/terminal/push', { method: 'POST', body: dto });
  },

  getTerminalPush(pushId: string) {
    return apiClient<TerminalPushState>(`/payments/terminal/push/${encodeURIComponent(pushId)}`);
  },

  cancelTerminalPush(pushId: string) {
    return apiClient<unknown>(`/payments/terminal/push/${encodeURIComponent(pushId)}/cancel`, {
      method: 'POST',
    });
  },

  // ─── Online payment ────────────────────────────────────────────────────────
  createOnlineOrder(billId: string) {
    return apiClient<CreateOnlineOrderResponse>('/payments/online/create-order', {
      method: 'POST',
      body: { bill_id: billId },
    });
  },

  verifyOnlinePayment(dto: VerifyOnlinePaymentRequest) {
    return apiClient<unknown>('/payments/online/verify', {
      method: 'POST',
      body: dto,
    });
  },

  // ─── Refunds ───────────────────────────────────────────────────────────────
  initiateRefund(dto: InitiateRefundRequest) {
    return apiClient<RazorpayRefund>('/payments/refunds', {
      method: 'POST',
      body: dto,
    });
  },

  getRefund(id: string) {
    return apiClient<RazorpayRefund>(`/payments/refunds/${encodeURIComponent(id)}`);
  },

  listRefundsForBill(billId: string) {
    return apiClient<RazorpayRefund[]>(`/payments/refunds?bill_id=${encodeURIComponent(billId)}`);
  },

  // ─── Gateway credentials (OWNER) ───────────────────────────────────────────
  getGatewayCredentials(provider: string = 'razorpay') {
    return apiClient<GatewayCredentialsMasked>(
      `/payments/gateway-credentials/${encodeURIComponent(provider)}`,
    );
  },

  upsertGatewayCredentials(input: {
    provider: string;
    key_id: string;
    key_secret: string;
    webhook_secret?: string;
    is_active?: boolean;
  }) {
    return apiClient<GatewayCredentialsMasked>('/payments/gateway-credentials', {
      method: 'PUT',
      body: input,
    });
  },

  testGatewayCredentials(provider: string = 'razorpay') {
    return apiClient<{ ok: boolean; error?: string }>(
      `/payments/gateway-credentials/${encodeURIComponent(provider)}/test`,
      { method: 'POST' },
    );
  },

  deactivateGatewayCredentials(provider: string = 'razorpay') {
    return apiClient<{ deactivated: boolean }>(
      `/payments/gateway-credentials/${encodeURIComponent(provider)}`,
      { method: 'DELETE' },
    );
  },

  // ─── Reconciliation (MANAGER+) ─────────────────────────────────────────────
  reconciliationSummary(from: string, to: string) {
    return apiClient<unknown>(
      `/payments/reconciliation/summary?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
  },

  reconciliationVariance(from: string, to: string) {
    return apiClient<unknown>(
      `/payments/reconciliation/variance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
  },

  reconciliationSettlements(from: string, to: string) {
    return apiClient<unknown>(
      `/payments/reconciliation/settlements?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
  },
};
